import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Copy, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { WhatsAppTemplatesCard } from '@/components/whatsapp-templates';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { TicketMeta } from '@/lib/types';
import { formatDateTime } from '@/lib/utils';

interface Option {
  id: string;
  label: string;
  areaId: string;
  categoryId?: string | null;
}
interface Config {
  configured: boolean;
  phoneNumberId: string | null;
  displayPhone: string | null;
  hasAccessToken: boolean;
  hasAppSecret: boolean;
  active: boolean;
  greeting: string;
  menuQuestion: string;
  confirmText: string;
  menuOptions: Option[];
  defaultAreaId: string | null;
  wabaId: string | null;
  webhookUrl: string | null;
  verifyToken: string | null;
  lastError: string | null;
  lastInboundAt: string | null;
}
interface Message {
  id: string;
  direction: 'IN' | 'OUT';
  status: string;
  phone: string;
  body: string;
  error: string | null;
  ticketId: string | null;
  createdAt: string;
}
const onError = (e: unknown) => toast.error(errorMessage(e));
const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => (b % 36).toString(36)).join('');

function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <Field label={label} htmlFor={`cp-${label}`}>
      <div className="flex gap-2">
        <Input id={`cp-${label}`} readOnly value={value} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          aria-label={`${label} kopieren`}
          onClick={() =>
            navigator.clipboard
              .writeText(value)
              .then(() => toast.success('Kopiert'))
              .catch(() => toast.error('Kopieren nicht möglich'))
          }
        >
          <Copy className="size-4" />
        </Button>
      </div>
    </Field>
  );
}

export function WhatsAppPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['whatsapp'], queryFn: () => api<Config>('/integrations/whatsapp') });
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta') });
  const { data: log } = useQuery({ queryKey: ['whatsapp', 'messages'], queryFn: () => api<{ items: Message[] }>('/integrations/whatsapp/messages'), enabled: Boolean(data?.configured) });
  const [form, setForm] = useState<(Config & { accessToken: string; appSecret: string }) | null>(null);
  const [testPhone, setTestPhone] = useState('');
  useEffect(() => {
    if (data) setForm({ ...data, accessToken: '', appSecret: '' });
  }, [data]);
  const set = <K extends keyof NonNullable<typeof form>>(k: K, v: NonNullable<typeof form>[K]) => setForm((f) => f && { ...f, [k]: v });
  const refresh = () => qc.invalidateQueries({ queryKey: ['whatsapp'] });
  const save = useMutation({
    mutationFn: () =>
      api('/integrations/whatsapp', {
        method: 'PUT',
        body: {
          phoneNumberId: form!.phoneNumberId ?? '',
          ...(form!.accessToken ? { accessToken: form!.accessToken } : {}),
          ...(form!.appSecret ? { appSecret: form!.appSecret } : {}),
          active: form!.active,
          greeting: form!.greeting,
          menuQuestion: form!.menuQuestion,
          confirmText: form!.confirmText,
          menuOptions: form!.menuOptions.map((o) => ({ ...o, categoryId: o.categoryId || null })),
          defaultAreaId: form!.defaultAreaId || null,
          wabaId: form!.wabaId || '',
        },
      }),
    onSuccess: () => {
      toast.success('WhatsApp gespeichert');
      refresh();
    },
    onError,
  });
  const test = useMutation({ mutationFn: () => api<{ ok: boolean; message: string }>('/integrations/whatsapp/test', { method: 'POST' }), onSuccess: (r) => ((r.ok ? toast.success : toast.error)(r.message), refresh()), onError });
  const testMsg = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>('/integrations/whatsapp/test-message', { method: 'POST', body: { phone: testPhone } }),
    onSuccess: (r) => ((r.ok ? toast.success : toast.error)(r.message), qc.invalidateQueries({ queryKey: ['whatsapp', 'messages'] })),
    onError,
  });
  if (!form || !meta) return <Skeleton className="h-96" />;
  const areas = meta.areas.filter((a) => a.active);
  const setOption = (i: number, o: Partial<Option>) => set('menuOptions', form.menuOptions.map((x, j) => (j === i ? { ...x, ...o } : x)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...form.menuOptions];
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    set('menuOptions', next);
  };

  return (
    <div className="grid gap-4">
      <PageHeader
        title="WhatsApp Business"
        description="Kunden schreiben per WhatsApp, eine einstellbare Auswahl ordnet das Thema zu und es entsteht ein Ticket. Interne Benachrichtigungen an Mitarbeiter-Handys legen Sie unter Automatisierungen fest."
        actions={
          <div className="flex gap-2">
            {data?.configured && (
              <Button variant="outline" onClick={() => test.mutate()} loading={test.isPending}>
                Verbindung prüfen
              </Button>
            )}
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              Speichern
            </Button>
          </div>
        }
      />
      {data?.lastError && <Alert variant="destructive">Letzter Fehler: {data.lastError}</Alert>}
      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              Zugang (Meta WhatsApp Cloud API) {data?.configured && (data.active ? <Badge variant="success">aktiv</Badge> : <Badge variant="outline">aus</Badge>)}
            </CardTitle>
            <CardDescription>
              Im Meta-Entwicklerportal (developers.facebook.com → Ihre App → WhatsApp → API-Einrichtung): Telefonnummern-ID, dauerhaftes Zugriffstoken eines Systembenutzers und das App-Geheimnis (Einstellungen → Allgemeines). Die Angaben
              werden verschlüsselt gespeichert.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Field label="Telefonnummern-ID" htmlFor="wa-phone">
              <Input id="wa-phone" value={form.phoneNumberId ?? ''} onChange={(e) => set('phoneNumberId', e.target.value)} placeholder="z. B. 123456789012345" />
            </Field>
            <Field label={form.hasAccessToken ? 'Zugriffstoken (leer = unverändert)' : 'Zugriffstoken'} htmlFor="wa-token">
              <Input id="wa-token" type="password" autoComplete="off" value={form.accessToken} onChange={(e) => set('accessToken', e.target.value)} />
            </Field>
            <Field label={form.hasAppSecret ? 'App-Geheimnis (leer = unverändert)' : 'App-Geheimnis'} htmlFor="wa-secret">
              <Input id="wa-secret" type="password" autoComplete="off" value={form.appSecret} onChange={(e) => set('appSecret', e.target.value)} />
            </Field>
            <Field label="WhatsApp-Business-Konto-ID (für „Vorlagen von Meta laden“)" htmlFor="wa-waba">
              <Input id="wa-waba" value={form.wabaId ?? ''} onChange={(e) => set('wabaId', e.target.value)} placeholder="z. B. 102345678901234" />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} /> Aktiv
            </label>
            {data?.displayPhone && <p className="text-sm text-muted-foreground">Nummer: {data.displayPhone}</p>}
            {data?.webhookUrl && data.verifyToken && (
              <div className="grid gap-2 rounded-md border bg-muted/40 p-3">
                <p className="text-sm font-medium">Webhook in Meta eintragen (WhatsApp → Konfiguration), Feld „messages“ abonnieren:</p>
                <CopyField label="Rückruf-URL" value={data.webhookUrl} />
                <CopyField label="Überprüfungstoken" value={data.verifyToken} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Neue Anfragen: Begrüßung und Auswahl</CardTitle>
            <CardDescription>Auf die erste Nachricht antwortet das Portal mit der Auswahl (bis 3 als Knöpfe, sonst als Liste, max. 10). Platzhalter: {'{{firma.name}}'}, in der Bestätigung {'{{ticket.nummer}}'}.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Field label="Begrüßung" htmlFor="wa-greet">
              <Textarea id="wa-greet" rows={2} value={form.greeting} onChange={(e) => set('greeting', e.target.value)} />
            </Field>
            <Field label="Frage" htmlFor="wa-q">
              <Input id="wa-q" value={form.menuQuestion} onChange={(e) => set('menuQuestion', e.target.value)} />
            </Field>
            <div className="grid gap-2">
              <span className="text-sm font-medium">Auswahl → Bereich/Kategorie</span>
              {form.menuOptions.map((o, i) => (
                <div key={o.id} className="grid gap-2 rounded-md border p-2 sm:grid-cols-[1fr_10rem_10rem_auto]">
                  <Input value={o.label} onChange={(e) => setOption(i, { label: e.target.value })} placeholder="z. B. Reparatur" aria-label="Bezeichnung" maxLength={60} />
                  <Select value={o.areaId} onChange={(e) => setOption(i, { areaId: e.target.value, categoryId: null })} aria-label="Bereich">
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                  <Select value={o.categoryId ?? ''} onChange={(e) => setOption(i, { categoryId: e.target.value || null })} aria-label="Kategorie">
                    <option value="">– ohne Kategorie –</option>
                    {meta.categories
                      .filter((c) => !c.areaId || c.areaId === o.areaId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </Select>
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="icon" className="size-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Nach oben">
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="size-8" disabled={i === form.menuOptions.length - 1} onClick={() => move(i, 1)} aria-label="Nach unten">
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="size-8 text-destructive hover:text-destructive" onClick={() => set('menuOptions', form.menuOptions.filter((_, j) => j !== i))} aria-label="Entfernen">
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {form.menuOptions.length < 10 && (
                <Button type="button" variant="outline" size="sm" className="justify-self-start" onClick={() => set('menuOptions', [...form.menuOptions, { id: newId(), label: '', areaId: areas[0]?.id ?? '', categoryId: null }])}>
                  <Plus /> Auswahl hinzufügen
                </Button>
              )}
              {form.menuOptions.length === 0 && (
                <Field label="Ohne Auswahl: Bereich für neue Anfragen" htmlFor="wa-default">
                  <Select id="wa-default" value={form.defaultAreaId ?? ''} onChange={(e) => set('defaultAreaId', e.target.value || null)}>
                    <option value="">– bitte wählen –</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
            <Field label="Bestätigung nach der Auswahl" htmlFor="wa-confirm">
              <Textarea id="wa-confirm" rows={2} value={form.confirmText} onChange={(e) => set('confirmText', e.target.value)} />
            </Field>
          </CardContent>
        </Card>

        {data?.configured && <WhatsAppTemplatesCard meta={meta} hasWaba={Boolean(data.wabaId)} />}

        {data?.configured && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Testnachricht</CardTitle>
              <CardDescription>Sendet eine interne Benachrichtigung an die Nummer (mit Vorlage, falls eingerichtet).</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+49 151 …" aria-label="Telefonnummer" />
              <Button variant="outline" onClick={() => testMsg.mutate()} loading={testMsg.isPending} disabled={testPhone.length < 6}>
                <Send /> Senden
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {data?.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Verlauf</CardTitle>
            <CardDescription>Letzte 100 Nachrichten (Nummern gekürzt). Letzte eingehende Nachricht: {formatDateTime(data.lastInboundAt)}</CardDescription>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>Zeit</TH>
                  <TH>Richtung</TH>
                  <TH>Nummer</TH>
                  <TH>Text</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <TBody>
                {log?.items.length === 0 && <EmptyRow colSpan={5}>Noch keine Nachrichten.</EmptyRow>}
                {log?.items.map((m) => (
                  <TR key={m.id}>
                    <TD className="whitespace-nowrap text-xs">{formatDateTime(m.createdAt)}</TD>
                    <TD>{m.direction === 'IN' ? 'eingehend' : 'ausgehend'}</TD>
                    <TD className="whitespace-nowrap font-mono text-xs">{m.phone}</TD>
                    <TD className="max-w-md truncate text-sm" title={m.body}>
                      {m.body}
                    </TD>
                    <TD>
                      <Badge variant={m.status === 'FAILED' ? 'destructive' : m.status === 'PENDING' ? 'outline' : 'secondary'} title={m.error ?? undefined}>
                        {m.status}
                      </Badge>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
